import { useEffect, useRef, useState } from "react";

import { i18nFilter } from "app/filters/i18nFilter";

import { validateEmail } from "../utils";

import {
    EmailField,
    EmailFieldContainer,
    EmailInputContainer,
    Messages,
    RemainingSeats,
    SeatLimitReached,
    UnknownDomain,
    AddButton,
} from "./EmailInput.styled";

interface IEmailInputProps {
    allowedDomains: string[];
    remainingSeatsTotal: number;
    remainingSeatsCount: number;
    isUnlimitedSeats?: boolean;
    handleAddEmail: (email: string) => void;
    isLoading?: boolean;
}

const EmailInput = ({
    allowedDomains,
    remainingSeatsTotal,
    remainingSeatsCount,
    isUnlimitedSeats = false,
    handleAddEmail,
    isLoading = false,
}: IEmailInputProps) => {
    const i18n = i18nFilter();
    const inputRef = useRef(null);

    const placeholder = i18n("growth.homepage.inviteUsersV2.modal.invite.email_placeholder");

    const [hasDomainError, setHasDomainError] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(false);

    const isNoMoreSeatsLeft = remainingSeatsCount <= 0;
    const isAddButtonDisabled = isLoading || isNoMoreSeatsLeft || hasDomainError || !isValidEmail;

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleChange = (value: string) => {
        inputRef.current?.setValue(value);
        const res = validateEmail(value, allowedDomains);
        setIsValidEmail(res.valid);
        setHasDomainError(res.invalidDomain);
    };

    const handleAddEmailButtonClick = (value: string) => {
        const res = validateEmail(value, allowedDomains);
        if (res.valid) {
            handleAddEmail(value);
            inputRef.current?.clearValue();
            setIsValidEmail(false);
        }
        setHasDomainError(res.invalidDomain);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && remainingSeatsCount > 0) {
            const value = (e.target as HTMLInputElement).value;
            handleAddEmailButtonClick(value);
        }
    };

    return (
        <EmailInputContainer>
            <RemainingSeats noSeatsLeft={isNoMoreSeatsLeft}>
                {isLoading ? (
                    i18n("growth.homepage.inviteUsersV2.modal.invite.loading")
                ) : (
                    <>
                        {i18n("growth.homepage.inviteUsersV2.modal.invite.seats_remaining")}: &nbsp;
                        {isUnlimitedSeats
                            ? i18n("growth.homepage.inviteUsersV2.modal.invite.unlimited")
                            : `${remainingSeatsCount}/${remainingSeatsTotal}`}
                    </>
                )}
            </RemainingSeats>
            <EmailFieldContainer isNoMoreSeatsLeft={isNoMoreSeatsLeft}>
                <EmailField
                    ref={inputRef}
                    placeholder={placeholder}
                    disabled={isLoading || isNoMoreSeatsLeft}
                    isNoMoreSeatsLeft={isNoMoreSeatsLeft}
                    hideBorder
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    isLoading
                />
                <AddButton
                    type="flat"
                    isDisabled={isAddButtonDisabled}
                    isNoMoreSeatsLeft={isNoMoreSeatsLeft}
                    onClick={() => handleAddEmailButtonClick(inputRef?.current?.getValue() || "")}
                >
                    {i18n("growth.homepage.inviteUsersV2.modal.invite.btn_email_add")}
                </AddButton>
            </EmailFieldContainer>
            <Messages>
                <>
                    {!isLoading && hasDomainError ? (
                        <UnknownDomain>
                            {i18n("growth.homepage.inviteUsersV2.modal.invite.error_domain")}
                            <span>{i18n("growth.homepage.inviteUsersV2.modal.invite.error_domain_2")}</span>
                        </UnknownDomain>
                    ) : null}
                    {!isLoading && remainingSeatsCount === 0 ? (
                        <SeatLimitReached>
                            {i18n("growth.homepage.inviteUsersV2.modal.invite.error_seats_limit_reached")}
                        </SeatLimitReached>
                    ) : null}
                </>
            </Messages>
        </EmailInputContainer>
    );
};

export default EmailInput;
